/**
 * Custom Chart Label Components
 * Provides better readability for chart labels with improved styling
 */

interface CustomPieLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  name?: string;
  value?: number;
}

/**
 * Renders position for a custom pie chart label
 */
const RADIAN = Math.PI / 180;

export function renderCustomPieLabel({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  name,
}: CustomPieLabelProps) {
  // Only show labels for segments > 3% to avoid clutter
  if (percent < 0.03) return null;

  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="text-sm font-bold"
      style={{
        fontSize: '14px',
        fontWeight: 700,
        paintOrder: 'stroke',
        stroke: 'rgba(0, 0, 0, 0.8)',
        strokeWidth: '3px',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

/**
 * Renders outer label for pie charts (positioned outside the chart)
 */
export function renderCustomOuterPieLabel({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  name,
}: CustomPieLabelProps) {
  // Only show labels for segments > 2% to avoid clutter
  if (percent < 0.02) return null;

  const radius = outerRadius + 30;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="hsl(var(--foreground))"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="text-xs font-semibold"
      style={{
        fontSize: '13px',
        fontWeight: 600,
      }}
    >
      {name ? `${name} ${(percent * 100).toFixed(0)}%` : `${(percent * 100).toFixed(0)}%`}
    </text>
  );
}
